import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Mail, Lock, ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authStatus, signIn, signUp } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine mode from URL or Path
  const [isLogin, setIsLogin] = useState(() => {
      const modeParam = searchParams.get('mode');
      if (modeParam) return modeParam === 'login';
      return !location.pathname.includes('/signup');
  });

  const goal = searchParams.get('g') || '';
  const plan = searchParams.get('plan') || '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showRequirements, setShowRequirements] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');

  // Password validation rules
  const requirements = [
    { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
    { label: 'Uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
    { label: 'Lowercase letter', test: (pw) => /[a-z]/.test(pw) },
    { label: 'Number', test: (pw) => /[0-9]/.test(pw) },
    { label: 'Symbol', test: (pw) => /[^A-Za-z0-9]/.test(pw) }
  ];
  const allRequirementsMet = requirements.every(r => r.test(password));
  const passwordsMatch = !isLogin && password && confirmPassword && password === confirmPassword;

  // Update URL when mode changes
  useEffect(() => {
    // This effect syncs the URL's 'mode' parameter with the isLogin state.
    const newParams = new URLSearchParams(searchParams);
    const newMode = isLogin ? 'login' : 'signup';
    
    if (newParams.get('mode') !== newMode) {
        newParams.set('mode', newMode);
        // We use { replace: true } to avoid polluting browser history.
        setSearchParams(newParams, { replace: true });
    }
  }, [isLogin, searchParams, setSearchParams]);

  // Failsafe to redirect already authenticated users
  useEffect(() => {
    if (authStatus === 'authenticated') {
      // 1. Priority: If a plan is selected, go to checkout
      if (plan) {
          console.log('[Auth] Redirecting to checkout with plan:', plan);
          navigate('/checkout', { state: { selectedPlan: plan }, replace: true });
          return;
      }

      // 2. Check location state (e.g. protected route redirect)
      const from = location.state?.from;
      console.log('[Auth] Post-login redirect. location.state.from:', from);
      if (from) {
        // Restore full path including query params
        const dest = from.pathname + (from.search || '');
        console.log('[Auth] Navigating to:', dest);
        navigate(dest, { replace: true });
      } else {
        // Fallback: check sessionStorage for intended destination
        let fallbackDest = null;
        try {
          fallbackDest = sessionStorage.getItem('podvault_post_login_redirect');
        } catch (e) {}
        if (fallbackDest) {
          console.log('[Auth] Navigating to fallback from sessionStorage:', fallbackDest);
          try { sessionStorage.removeItem('podvault_post_login_redirect'); } catch (e) {}
          navigate(fallbackDest, { replace: true });
        } else {
          console.log('[Auth] Navigating to default /app/projects');
          navigate('/app/projects', { replace: true });
        }
      }
    }
  }, [authStatus, navigate, location.state]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setIsProcessing(true);

    if (!email || !password) {
      setAuthError('Email and password are required.');
      setIsProcessing(false);
      return;
    }

    if (!isLogin) {
      // Registration
      if (!allRequirementsMet) {
        setAuthError('Password does not meet all requirements.');
        setIsProcessing(false);
        return;
      }
      if (password !== confirmPassword) {
        setAuthError("Passwords don't match.");
        setIsProcessing(false);
        return;
      }
      try {
        const { error } = await signUp(email, password);
        if (error) {
          throw error;
        }
        setAuthSuccess('Check your email for the confirmation link!');
      } catch (error) {
        setAuthError(error.message);
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // Login
    try {
      const { error } = await signIn(email, password);
      if (error) {
        throw error;
      }
    } catch (err) {
      setAuthError('Unexpected error, please try again.');
    }
    setIsProcessing(false);
  };

  const toggleMode = (e) => {
    e.preventDefault();
    setIsLogin(!isLogin);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Simple Nav */}
      <div className="p-6">
        <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 pb-20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">
              {isLogin ? 'Log in to PodVault' : 'Create your account'}
            </h1>
            <p className="text-slate-400">
              {isLogin ? "Welcome back! Let's get you to your insights." : 'Unlock powerful podcast insights in seconds.'}
            </p>
            {goal && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 border border-primary/30 text-sm text-primary">
                <span>Recently exploring:</span>
                <span className="font-semibold text-white">{goal}</span>
              </div>
            )}
          </div>

          <div className="bg-surface p-8 rounded-2xl border border-white/10 shadow-2xl">
            <form onSubmit={handleAuth} className="space-y-6">
              <div>
                <label htmlFor="email" className="text-sm font-medium text-slate-400 block mb-2">Email</label>
                <div className="relative">
                  <Mail className="w-5 h-5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full bg-black/20 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-white focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="text-sm font-medium text-slate-400 block mb-2">Password</label>
                <div className="relative">
                  <Lock className="w-5 h-5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (!isLogin) setShowRequirements(true);
                    }}
                    placeholder={isLogin ? "••••••••" : "At least 8 characters"}
                    required
                    className="w-full bg-black/20 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-white focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                    autoComplete="new-password"
                  />
                </div>
                {isLogin && (
                  <div className="mt-2 text-right">
                    <Link to="/forgot-password" className="text-xs text-slate-400 hover:text-primary">
                      Forgot your password?
                    </Link>
                  </div>
                )}
                {/* Password requirements checklist (sign-up only) */}
                {!isLogin && showRequirements && (
                  <ul className="mt-2 space-y-1 text-xs">
                    {requirements.map((r, idx) => {
                      const met = r.test(password);
                      return (
                        <li key={idx} className={met ? "text-green-400" : "text-red-400"}>
                          {met ? "✅" : "❌"} {r.label}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Confirm password field for sign-up */}
              {!isLogin && (
                <div>
                  <label htmlFor="confirmPassword" className="text-sm font-medium text-slate-400 block mb-2">Confirm Password</label>
                  <div className="relative">
                    <Lock className="w-5 h-5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      required
                      className="w-full bg-black/20 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-white focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                      autoComplete="new-password"
                    />
                  </div>
                  {confirmPassword && (
                    <p className={passwordsMatch ? "text-green-400 text-xs mt-1" : "text-red-400 text-xs mt-1"}>
                      {passwordsMatch ? "✅ Passwords match" : "❌ Passwords don't match"}
                    </p>
                  )}
                </div>
              )}

              {/* Error and success feedback */}
              {authError && (
                <div className="text-red-400 text-xs mb-2 text-center bg-red-500/10 p-2 rounded border border-red-500/20">{authError}</div>
              )}
              {authSuccess && (
                <div className="text-green-400 text-xs mb-2 text-center bg-green-500/10 p-2 rounded border border-green-500/20">{authSuccess}</div>
              )}

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary text-white font-bold rounded-lg transition-all duration-300 hover:shadow-glow-blue hover:bg-primary-hover transform hover:-translate-y-0.5"
                disabled={isProcessing || (!isLogin && (!allRequirementsMet || !passwordsMatch))}
              >
                {isProcessing ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>{isLogin ? 'Log In' : 'Create Account'}</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-sm text-slate-500 mt-6">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button onClick={toggleMode} className="font-bold text-primary hover:text-white hover:underline bg-transparent border-none p-0 cursor-pointer transition-colors">
              {isLogin ? 'Sign Up' : 'Log In'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
